import { useEffect, useMemo, useState, type JSX } from 'react';
import { useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Spread } from '../shared/components/Spread.js';
import { RevealCast } from './components/RevealCast.js';
import { RevealPlate } from './components/RevealPlate.js';
import type { RoleRevealView } from './types.js';
import './reveal-arc.css';

export interface RoleRevealVersoProps {
  view: RoleRevealView;
  step: 0 | 1 | 2;
}

/**
 * The RoleReveal verso content — extracted as a named export so GameEnd
 * can mount it on its closed-spread verso during the close cinematic
 * (with step=2, the resolved state). RoleReveal itself still uses this
 * via its internal render so the parent component is unchanged.
 */
export function RoleRevealVerso({ view, step }: RoleRevealVersoProps): JSX.Element {
  const { t } = useTranslation();
  const struckIds = useMemo(
    () => new Set<string>([view.eliminated.player.id, ...view.alsoEliminated.map((e) => e.player.id)]),
    [view.eliminated, view.alsoEliminated],
  );
  return (
    <div className="susdle-reveal-verso">
      <p className="susdle-reveal-verso__spoken">{t('reveal.roleReveal.spoken')}</p>
      <RevealCast players={view.cast} struckIds={struckIds} step={step} />
    </div>
  );
}

export interface RoleRevealRectoProps {
  view: RoleRevealView;
  step: 0 | 1 | 2;
}

/**
 * The RoleReveal recto content — extracted as a named export so GameEnd
 * can mount it on its closing leaf's recto face during the close cinematic
 * (with step=2, the carte-de-visite plate visible). RoleReveal itself uses
 * this via its internal render so the parent component is unchanged.
 *
 * Live region: the plate's elimination line is announced when it inserts at
 * step 2 (the pending placeholder is empty/aria-hidden before then). A true
 * cold arrival starts at step 2, so it doesn't re-announce already-present
 * state — consistent with the Cold-Arrival Rule.
 */
export function RoleRevealRecto({ view, step }: RoleRevealRectoProps): JSX.Element {
  return (
    <div className="susdle-reveal-recto" role="status" aria-live="polite">
      {step === 2 ? (
        <RevealPlate eliminated={view.eliminated} alsoEliminated={view.alsoEliminated} />
      ) : (
        <div className="susdle-reveal-recto__pending" aria-hidden />
      )}
    </div>
  );
}

interface RoleRevealProps {
  view: RoleRevealView;
  /** Cold arrival (reconnect / boot into the reveal): jump straight to the
   *  resolved end-state, no choreography. Default `false`. */
  justArrived?: boolean;
}

/**
 * RoleReveal — the choreography printed onto the open spread (never a modal):
 *   step 0 — "The village has spoken."; the cast still present, the eliminated
 *            struck in Oxblood, clues visible.
 *   step 1 — the struck cells fade + shrink out; the grid reflows to the living.
 *   step 2 — the recto resolves to the identity reveal (the face-up figure).
 * Reduced motion / cold arrival jump to step 2.
 *
 * Palette is warm-candlelit for every viewer — the reveal is a public staged
 * beat, not a per-POV surface (the Drenched exception, brief §4). The Ghost's
 * Spectral accent lives on the reveal plate, not the page.
 */
export function RoleReveal({ view, justArrived = false }: RoleRevealProps): JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  /* Latch the cold-arrival flag at mount. `justArrived` from usePhaseRouter
     is a single-render signal: true on the first render after a cold arrival,
     false on every subsequent render once the prevPhase ref updates. If the
     effect below depended on the prop directly, a later prop flip to false
     (triggered by any ambient state patch — connected toggle, clock skew,
     etc. — during the cinematic window) would re-run the effect's ELSE
     branch and play the resolved → exiting → resolved sequence BACKWARDS.
     Latching once at mount makes the deps stable. */
  const [coldArrival] = useState(justArrived);
  const [step, setStep] = useState<0 | 1 | 2>(coldArrival ? 2 : 0);

  useEffect(() => {
    if (coldArrival || reduceMotion) {
      setStep(2);
      return undefined;
    }
    const toExiting = window.setTimeout(() => setStep(1), 900);
    const toResolved = window.setTimeout(() => setStep(2), 1400);
    return () => {
      window.clearTimeout(toExiting);
      window.clearTimeout(toResolved);
    };
  }, [coldArrival, reduceMotion]);

  return (
    <div className="susdle-reveal" data-mood="candle" data-testid="role-reveal">
      <h1 className="susdle-sr-only">{t('reveal.roleReveal.ariaLabel')}</h1>
      <Spread
        pov="village"
        ariaLabel={t('reveal.roleReveal.ariaLabel')}
        overlay={<div className="susdle-spread-candle" data-mood="reveal" aria-hidden />}
        verso={<RoleRevealVerso view={view} step={step} />}
        recto={<RoleRevealRecto view={view} step={step} />}
      />
    </div>
  );
}
